import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, UserPlus, Check, Ban, Globe, Phone, Link as LinkIcon, Clock, Search, Music2, Archive, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAccessRequests, decideAccessRequest } from "@/lib/afmApi";
import { Loading, SectionTitle } from "@/components/common";
import { fmtDate } from "@/lib/format";

const ADMIN_ROLES = ["admin", "distribution_admin", "superadmin", "staff"];
const FILTERS = [
  { key: "", label: "Todas" },
  { key: "pending_approval", label: "Pendientes" },
  { key: "approved", label: "Aprobadas" },
  { key: "rejected", label: "Rechazadas" },
  { key: "discarded", label: "Descartadas" },
];
const STATUS = {
  pending_approval: { label: "Pendiente de revisión", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Aprobada", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rechazada", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  discarded: { label: "Descartada", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function AdminAccessRequests() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role) || user?.super_admin || user?.distribution_admin;
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [filter, setFilter] = useState("pending_approval");
  const [busy, setBusy] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const shown = useMemo(() => {
    let l = items;
    const s = q.trim().toLowerCase();
    if (s) l = l.filter((r) => `${r.name} ${r.email} ${r.country} ${r.artist_name || ""}`.toLowerCase().includes(s));
    return [...l].sort((a, b) => (sort === "country" ? (a.country || "").localeCompare(b.country || "") : new Date(b.created_at) - new Date(a.created_at)));
  }, [items, q, sort]);

  const load = useCallback(() => {
    if (!isAdmin) { setStatus("denied"); return; }
    setStatus("loading");
    getAccessRequests(user.id, filter)
      .then((d) => { setItems(Array.isArray(d) ? d : []); setStatus("ok"); })
      .catch((e) => setStatus(e?.response?.status === 403 ? "denied" : "error"));
  }, [user.id, filter, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action, admin_note = "") => {
    setBusy(id);
    try {
      await decideAccessRequest(id, { viewer_id: user.id, action, admin_note });
      toast.success(action === "approve" ? "Acceso aprobado · cuenta creada" : action === "discard" ? "Solicitud descartada (sin correo)" : "Solicitud rechazada");
      setRejecting(null); setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo procesar la solicitud");
    } finally { setBusy(""); }
  };

  if (status === "loading") return <Loading />;
  if (status === "denied") return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="ar-admin-denied">
      <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
      <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-500">Esta sección es exclusiva del equipo de Always Fresh.</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-access-requests-page">
      <SectionTitle>Solicitudes de Acceso</SectionTitle>
      <p className="max-w-2xl text-sm text-slate-500">Nuevos artistas que solicitaron acceso. Al aprobar, se crea su cuenta en el distribuidor y podrán iniciar sesión.</p>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((ff) => (
          <button key={ff.key || "all"} data-testid={`ar-filter-${ff.key || "all"}`} onClick={() => setFilter(ff.key)} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${filter === ff.key ? "border-black bg-black text-white" : "border-slate-300 bg-white text-slate-600 hover:border-black"}`}>{ff.label}</button>
        ))}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#171717]/40" />
          <input data-testid="ar-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, email o país…" className="w-full rounded-lg border border-[#E5E5E5]/20 bg-[#FFFFFF] py-2 pl-9 pr-3 text-sm outline-none focus:border-[#E5E5E5]" />
        </div>
        <select data-testid="ar-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-[#E5E5E5]/20 bg-[#FFFFFF] px-3 py-2 text-sm outline-none focus:border-[#E5E5E5]">
          <option value="recent">Más recientes</option>
          <option value="country">Por país (A–Z)</option>
        </select>
      </div>

      {!shown.length ? (
        <p className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-16 text-center text-sm text-slate-400">Sin solicitudes en esta categoría.</p>
      ) : (
        <div className="space-y-4">
          {shown.map((r) => {
            const b = STATUS[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
            return (
              <div key={r.id} data-testid={`ar-item-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-serif text-lg font-bold text-black">{r.name}</h3>
                      {r.artist_name && <span className="text-sm text-slate-500">· {r.artist_name}</span>}
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${b.cls}`}>{r.status === "pending_approval" && <Clock className="h-3 w-3" />}{b.label}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="font-mono">{r.email}</span>
                      {r.country && <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" /> {r.country}</span>}
                      {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>}
                      <span>· {fmtDate(r.created_at)}</span>
                    </div>
                    {/* Perfil de artista que eligió al registrarse */}
                    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`ar-profile-${r.id}`}>
                      {r.artist_image ? <img src={r.artist_image} alt="" className="h-10 w-10 rounded-full object-cover" /> : <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200"><Music2 className="h-4 w-4 text-slate-500" /></div>}
                      <div className="min-w-0 text-xs">
                        <div className="font-semibold text-black">{r.artist_name || "Sin nombre artístico"}</div>
                        <div className="mt-0.5 flex flex-wrap gap-3">
                          {r.spotify_url ? <a href={r.spotify_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#15803d] hover:underline">Spotify <ExternalLink className="h-3 w-3" /></a> : null}
                          {r.apple_url ? <a href={r.apple_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-black hover:underline">Apple Music <ExternalLink className="h-3 w-3" /></a> : null}
                          {!r.spotify_url && !r.apple_url && <span className="text-slate-500">{r.spotify_id || r.apple_id ? "Perfil elegido sin enlace" : "No eligió perfil de Spotify/Apple (artista nuevo o solicitud anterior al arreglo)"}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                      {r.social_link ? <a href={/^https?:\/\//.test(r.social_link) ? r.social_link : `https://${r.social_link}`} target="_blank" rel="noreferrer" data-testid={`ar-social-${r.id}`} className="inline-flex items-center gap-1.5 font-semibold text-[#9E1B22] hover:underline"><LinkIcon className="h-3.5 w-3.5" /> {r.social_link}</a> : <span className="text-slate-400">Sin red social</span>}
                      {!r.phone && <span className="text-slate-400">Sin teléfono</span>}
                    </div>
                    {r.admin_note && <p className="mt-2 text-xs text-slate-500">Nota: {r.admin_note}</p>}
                  </div>
                </div>

                {r.status === "pending_approval" && (
                  rejecting === r.id ? (
                    <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`ar-reject-form-${r.id}`}>
                      <textarea data-testid={`ar-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Motivo del rechazo (opcional)" className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black" />
                      <div className="flex gap-2">
                        <button data-testid={`ar-reject-confirm-${r.id}`} disabled={busy === r.id} onClick={() => decide(r.id, "reject", note.trim())} className="rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">Confirmar rechazo</button>
                        <button onClick={() => { setRejecting(null); setNote(""); }} className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium hover:border-black">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button data-testid={`ar-approve-${r.id}`} disabled={busy === r.id} onClick={() => decide(r.id, "approve")} className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Aprobar y crear cuenta</button>
                      <button data-testid={`ar-reject-${r.id}`} disabled={busy === r.id} onClick={() => { setRejecting(r.id); setNote(""); }} className="flex items-center gap-1.5 rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 disabled:opacity-50"><Ban className="h-3.5 w-3.5" /> Rechazar</button>
                      <button data-testid={`ar-discard-${r.id}`} disabled={busy === r.id} onClick={() => decide(r.id, "discard", "Prueba o spam")} title="Para pruebas o spam: se archiva sin enviar correo" className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-50"><Archive className="h-3.5 w-3.5" /> Descartar (prueba/spam)</button>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
